import { useEffect, useMemo } from 'react'

import type { ProcurementPlanRetailer } from '@ebalance/proto/bffuser/v1/procurement_plan_model_pb'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router'

import { CompactAlert } from '~/components/common/CompactAlert'
import { PageBody } from '~/components/common/PageBody'
import { PageLayout } from '~/components/common/PageLayout'
import { PageScrollArea } from '~/components/common/PageScrollArea'
import { RegionLoading } from '~/components/common/RegionLoading'
import {
  StatusHeaderCard,
  type StatusHeaderItem,
  statusHeaderValueStyle,
} from '~/components/planSubmit/StatusHeaderCard'
import { formatDateValue, fromProtoDate } from '~/models/dateTime'
import { KOMA_TIME_LABELS } from '~/models/komaToTime'
import { DetailFetchError } from '~/pages/ProcurementPlanDetail/components/DetailFetchError'
import {
  BG_ALL_SELECTION,
  DetailFilterBar,
} from '~/pages/ProcurementPlanDetail/components/DetailFilterBar'
import { buildUnitHeaderItem } from '~/pages/ProcurementPlanDetail/components/DetailHeader'
import { HandlingMemoPanel } from '~/pages/ProcurementPlanDetail/components/HandlingMemoPanel'
import { MismatchNavBanner } from '~/pages/ProcurementPlanDetail/components/MismatchNavBanner'
import { PlanTable } from '~/pages/ProcurementPlanDetail/components/PlanTable'
import { useDeeplinkHighlight } from '~/pages/ProcurementPlanDetail/hooks/useDeeplinkHighlight'
import { useDetailFetchFailure } from '~/pages/ProcurementPlanDetail/hooks/useDetailFetchFailure'
import { useGetNoSubmissionRecordPlan } from '~/pages/ProcurementPlanDetail/hooks/useGetNoSubmissionRecordPlan'
import { useGetPlanCounterpartyMismatch } from '~/pages/ProcurementPlanDetail/hooks/useGetPlanCounterpartyMismatch'
import { useMismatchNavigation } from '~/pages/ProcurementPlanDetail/hooks/useMismatchNavigation'
import type { SlotValue } from '~/pages/ProcurementPlanDetail/models/calc'
import type { BreakdownColumn } from '~/pages/ProcurementPlanDetail/models/columns'
import { buildNoSubmissionRecordTable } from '~/pages/ProcurementPlanDetail/models/detailTable'
import {
  buildVersionDetailRedirectUrl,
  parseNoSubmissionRecordPlanQuery,
} from '~/pages/ProcurementPlanDetail/models/noSubmissionRecordPlanQuery'
import { routePaths } from '~/routes/routePaths'

/** 種別は BG だけ (構成小売電気事業者は選べない。AC-PPD-028)。 */
const NO_RETAILERS: ProcurementPlanRetailer[] = []
/** 前回提出が無いので需要の前回値・差分は出さない。 */
const NO_PREVIOUS_SLOTS = Array<SlotValue>(48).fill(undefined)
const NO_DIFF_FLAGS = Array<boolean>(48).fill(false)
/** 計画を表示する前 (不整合の箇所を組む列が無い) */
const NO_COLUMNS: BreakdownColumn[] = []

/**
 * eBalance に提出記録の無い計画の詳細 (spec procurement-plan-detail.md AC-PPD-028、
 * issue #10266)。
 *
 * 提出管理の取引先カード (AC-PSM-038) から業務キー (`?targetDate=&submissionEntityCode=`)
 * で開き、広域機関の不整合通知の取引先別・コマ別の値を読み取り専用で出す。版の詳細とは
 * 別の画面で、提出・前回差分・誘導メッセージ・ステータスバッジ・ワークフローの戻り導線は
 * 持たない。
 *
 * - 業務キーに代表の版がある (開いた後に版ができた) 時は、ハイライト query を保ったまま
 *   版の詳細へ置き換え遷移する (AC-PPD-029)
 * - 取得失敗・NOT_FOUND・機能 OFF・URL 不正は、版の詳細と同じ取得失敗の表示にする
 */
export const ProcurementPlanNoSubmissionRecordPage = (): React.ReactElement => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const queryString = searchParams.toString()
  const parsed = useMemo(
    () => parseNoSubmissionRecordPlanQuery(new URLSearchParams(queryString)),
    [queryString],
  )
  const planKey = parsed?.planKey
  // 計画が変わった時に対応メモの入力・ハイライトを持ち越さないための計画の識別子。
  const planIdentity =
    parsed === undefined
      ? ''
      : `${formatDateValue(parsed.targetDate)}/${parsed.planKey.submissionEntityCode}`

  const { data, error, errorUpdatedAt, isFetching, refetch } =
    useGetNoSubmissionRecordPlan(planKey)
  const { hasFailed, isNotFound } = useDetailFetchFailure({
    data,
    error,
    errorUpdatedAt,
  })

  // 空文字の版の ID は値の無い応答と同じ扱い (取得失敗の表示) にする。
  const versionId =
    data?.featureEnabled === true &&
    data.result.case === 'planSubmissionId' &&
    data.result.value !== ''
      ? data.result.value
      : undefined
  const plan =
    data?.featureEnabled === true && data.result.case === 'plan'
      ? data.result.value
      : undefined

  useEffect(() => {
    if (versionId === undefined) return
    void navigate(
      buildVersionDetailRedirectUrl(
        versionId,
        new URLSearchParams(queryString),
      ),
      { replace: true },
    )
  }, [versionId, queryString, navigate])

  // 取得失敗 (成功データ無しのまま失敗した)・機能 OFF・結果なし・URL 不正。取得失敗以外は
  // 恒久的に表示できないので NotFound と同じ文言にする。
  const hasNoPlan =
    data !== undefined && plan === undefined && versionId === undefined
  const showFetchError = planKey === undefined || hasFailed || hasNoPlan

  const table = useMemo(
    () =>
      plan === undefined
        ? undefined
        : buildNoSubmissionRecordTable({
            procurementRows: plan.procurementRows,
            salesRows: plan.salesRows,
            translate: t,
          }),
    [plan, t],
  )

  // AC-PPD-028: 不整合の取引先・コマの強調とバナー (AC-PPD-009〜017)。対応メモパネルと同じ
  // read (同じ query key なので取得は 1 回)。通知の計画を表示する時だけ読む (版の詳細へ
  // 置き換える時・取得失敗の表示では読まない)。
  const counterpartyMismatch = useGetPlanCounterpartyMismatch(
    parsed === undefined || plan === undefined
      ? { planSubmissionId: '' }
      : { planKey: parsed.planKey },
  )
  const mismatchNav = useMismatchNavigation({
    mismatch: counterpartyMismatch.data,
    procurementColumns: table?.procurementColumns ?? NO_COLUMNS,
    salesColumns: table?.salesColumns ?? NO_COLUMNS,
    planIdentity,
  })

  // AC-PPD-022 / AC-PSM-038-1: 取引先カードから不整合範囲付きで開かれた時の一時強調。
  const deeplinkHighlight = useDeeplinkHighlight({
    isReady: table !== undefined,
    planId: planIdentity,
  })

  const breadcrumbItems = [
    { label: t('common:nav.plan'), to: routePaths.planProcurement.path },
    {
      label: t('common:route.planProcurement'),
      to: routePaths.planProcurement.path,
    },
    {
      label: t('common:route.planProcurementNoSubmissionRecord'),
      bold: true,
    },
  ]

  const renderBody = (): React.ReactNode => {
    // 取得失敗の Compact Alert は `notices` に出す (ページ全体の通知)。本文は何も描画しない
    if (showFetchError) return null
    if (plan === undefined || table === undefined || parsed === undefined) {
      return <RegionLoading minHeight="200px" isLoading />
    }
    const noRecordText = t(
      'page:procurementPlanDetail.header.noSubmissionRecord',
    )
    // AC-PPD-028: ステータスバッジ・作成・前回提出は出さず、BG コード欄に通知の提出者
    // コード、最終提出・提出者に「eBalance での提出記録なし」を出す。
    const headerItems: StatusHeaderItem[] = [
      {
        key: 'bgCode',
        label: t('page:procurementPlanDetail.header.bgCode'),
        value: (
          <span
            style={{
              ...statusHeaderValueStyle,
              fontFamily: 'monospace',
              fontWeight: 600,
            }}
          >
            {plan.submissionEntityCode}
          </span>
        ),
      },
      {
        key: 'lastSubmitted',
        label: t('page:procurementPlanDetail.header.lastSubmitted'),
        value: <span style={statusHeaderValueStyle}>{noRecordText}</span>,
      },
      {
        key: 'submittedBy',
        label: t('page:procurementPlanDetail.header.submittedBy'),
        value: <span style={statusHeaderValueStyle}>{noRecordText}</span>,
      },
      buildUnitHeaderItem(t),
    ]

    return (
      <>
        <DetailFilterBar
          area={plan.areaCode}
          date={
            plan.targetDate !== undefined
              ? fromProtoDate(plan.targetDate)
              : parsed.targetDate
          }
          readOnly
          retailers={NO_RETAILERS}
          selectedRetailer={BG_ALL_SELECTION}
        />
        <StatusHeaderCard items={headerItems} />
        <HandlingMemoPanel
          key={planIdentity}
          target={{ planKey: parsed.planKey }}
        />
        <MismatchNavBanner {...mismatchNav.banner} />
        <PageScrollArea>
          <PlanTable
            balanceDiffFlags={NO_DIFF_FLAGS}
            balances={table.balances}
            deeplinkHighlight={deeplinkHighlight}
            demandDiffFlags={NO_DIFF_FLAGS}
            demandPreviousSlots={NO_PREVIOUS_SLOTS}
            demandSlots={table.demandSlots}
            displayMode="perCounterpartyTotal"
            hasPreviousSubmitted={false}
            mismatch={mismatchNav.table}
            procurementColumns={table.procurementColumns}
            procurementTotals={table.procurementTotals}
            salesColumns={table.salesColumns}
            salesTotals={table.salesTotals}
            timeLabels={KOMA_TIME_LABELS}
          />
        </PageScrollArea>
      </>
    )
  }

  // ページ最上部の通知 (alert-placement.md §0・§2: 取得失敗 → 閲覧のみの案内)
  const pageNotices = showFetchError ? (
    <DetailFetchError
      isNotFound={!hasFailed || isNotFound}
      isReloading={isFetching}
      onReload={
        planKey === undefined
          ? undefined
          : () => {
              void refetch()
            }
      }
    />
  ) : plan === undefined ||
    table === undefined ||
    parsed === undefined ? undefined : (
    <CompactAlert
      description={t(
        'page:procurementPlanDetail.noSubmissionRecord.readOnlyNoticeDescription',
      )}
      status="info"
      title={t(
        'page:procurementPlanDetail.noSubmissionRecord.readOnlyNoticeTitle',
      )}
    />
  )

  return (
    <PageLayout breadcrumbItems={breadcrumbItems} notices={pageNotices}>
      <PageBody>{renderBody()}</PageBody>
    </PageLayout>
  )
}
